import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { CircularProgress } from '@mui/material';
import { closeModal, openModal } from 'store/portalModalDialogSlice';
import { sanitizeSlug } from 'utils/portalUtils';
import { z } from 'zod';

import { usePages } from '../../../api/portal/usePagesApi';
import DatePicker from '../../../components/DatePicker/DatePicker';
import { PORTAL_MODAL_NAMES } from '../../../components/DialogModalManager/constants/modalConfigs';
import FormHeader from '../../../components/FormHeader/FormHeader';
import Input from '../../../components/Input/Input';
import InputSelectDropdown from '../../../components/InputSelectDropdown/InputSelectDropdown';
import { cn } from '../../../components/lib/utils';
import RichText from '../../../components/RichText/RichText';
import Switch from '../../../components/Switch/Switch';
import Textarea from '../../../components/Textarea/Textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '../../../components/ui';
import UploadImage from '../../../components/UploadImage/UploadImage';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import { PagesFormProps } from '../types';

import {
  EPagesRadioButtonOptions,
  EPagesTemplateOptions,
  EPagesVisibilityOptions,
  FORM_SCHEMA,
  PAGES_LEVEL_OPTIONS,
  PAGES_RADIO_BUTTON_OPTIONS,
  pageTemplateOptions,
  visibilityOptions
} from './constants';

const PagesForm: FC<PagesFormProps> = ({ edit = false, dup = false }) => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { addPage, updatePage, getPage, getPages } = usePages(pageId);

  const dispatch = useDispatch();

  const DEFAULT_VALUES = useMemo(
    () => ({
      pageName: '',
      pageIcon: '',
      slug: '',
      pageTemplate: EPagesTemplateOptions.CONTENT,
      priority: 0,
      subPagePriority: 0,
      visibility: EPagesVisibilityOptions.PUBLIC,
      metadataImage: '',
      metadataDescription: '',
      state: EPagesRadioButtonOptions.HIDDEN,
      publishDate: '',
      isPublishDateEnabled: false,
      isSubPage: false,
      parentPageId: undefined
    }),
    []
  );

  const BUTTON_TEXT = edit ? 'Update' : 'Save';

  const form = useForm<z.infer<typeof FORM_SCHEMA>>({
    resolver: zodResolver(FORM_SCHEMA),
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES
  });

  const {
    formState: { errors, isValid, isDirty }
  } = form;

  const initializeForm = useCallback(
    (data: Partial<typeof DEFAULT_VALUES>) => {
      let isPublishDateEnabled = false;
      let state = data.state;

      if (
        data.state === EPagesRadioButtonOptions.SCHEDULED &&
        data.publishDate
      ) {
        isPublishDateEnabled = true;
        state = EPagesRadioButtonOptions.HIDDEN;
      }

      const {
        priority: dataPriority,
        subPagePriority: dataSubPagePriority,
        ...restData
      } = data;

      const priority = data.isSubPage ? undefined : dataPriority;
      const subPagePriority = data.isSubPage
        ? dataSubPagePriority ?? dataPriority
        : undefined;

      form.reset({
        ...DEFAULT_VALUES,
        ...restData,
        state,
        isPublishDateEnabled,
        parentPageId:
          data.parentPageId === null ? undefined : data.parentPageId,
        ...(priority !== undefined && { priority }),
        ...(subPagePriority !== undefined && { subPagePriority })
      });

      if (isPublishDateEnabled !== form.getValues('isPublishDateEnabled')) {
        form.setValue('isPublishDateEnabled', isPublishDateEnabled);
      }
    },
    [DEFAULT_VALUES, form]
  );

  useEffect(() => {
    if (edit && getPage.data) {
      initializeForm(getPage.data);
    } else if (dup && getPage.data) {
      initializeForm({
        ...getPage.data,
        pageName: `${getPage.data.pageName}_duplicated`
      });
    }
  }, [edit, dup, getPage.data, form, initializeForm]);

  const pageNameValue = form.watch('pageName');

  const isUpdateButtonEnabled = useMemo(() => {
    if (dup) return isValid;
    return isValid && isDirty;
  }, [dup, isValid, isDirty]);

  const isPageTemplateDisabled =
    edit &&
    (form.watch('pageTemplate') === EPagesTemplateOptions.HOME ||
      form.watch('pageTemplate') === EPagesTemplateOptions.STORE);

  const mainPages = useMemo(() => {
    if (!getPages.data?.pages) return [];

    return getPages.data.pages
      .filter((page: any) => page.isSubPage === false && page.id !== pageId)
      .map((page: any) => ({
        value: page.id,
        id: page.id,
        label: page.pageName
      }));
  }, [getPages.data, pageId]);

  useEffect(() => {
    if (pageNameValue && !isPageTemplateDisabled) {
      const formattedSlug = sanitizeSlug(pageNameValue);
      form.setValue('slug', formattedSlug);
    }
  }, [form, pageNameValue, isPageTemplateDisabled]);

  const onCloseForm = () => {
    navigate('../');
  };

  const handleIsSubPageChange = useCallback(
    (selectedValue: string, onChange: (value: boolean) => void) => {
      const isSubPageValue = selectedValue === 'true';
      onChange(isSubPageValue);

      const currentIsSubPage = form.getValues('isSubPage');
      const currentPriority = form.getValues('priority');
      const currentSubPagePriority = form.getValues('subPagePriority');

      if (!isSubPageValue) {
        form.setValue('parentPageId', undefined, {
          shouldDirty: true,
          shouldValidate: true
        });
        form.setValue('priority', currentSubPagePriority ?? 0, {
          shouldDirty: true,
          shouldValidate: true
        });
        form.setValue('subPagePriority', 0, {
          shouldDirty: true,
          shouldValidate: true
        });
      } else if (currentIsSubPage === false) {
        if (!form.getValues('parentPageId') && mainPages.length > 0) {
          form.setValue('parentPageId', mainPages[0].value, {
            shouldDirty: true,
            shouldValidate: true
          });
        }
        form.setValue('subPagePriority', currentPriority ?? 0, {
          shouldDirty: true,
          shouldValidate: true
        });
        form.setValue('priority', 0, {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    },
    [form, mainPages]
  );

  const onSubmit = async (data: z.infer<typeof FORM_SCHEMA>) => {
    const { isPublishDateEnabled, ...submitData } = data;

    if (submitData.state === EPagesRadioButtonOptions.VISIBLE) {
      delete submitData.publishDate;
    } else if (submitData.state === EPagesRadioButtonOptions.HIDDEN) {
      if (isPublishDateEnabled && submitData.publishDate) {
        submitData.state = EPagesRadioButtonOptions.SCHEDULED;
      } else {
        delete submitData.publishDate;
      }
    }

    if (!submitData.isSubPage) {
      submitData.parentPageId = null;
      submitData.subPagePriority = null;
      submitData.priority = submitData.priority ?? null;
    } else {
      if (submitData.parentPageId === undefined) {
        submitData.parentPageId = null;
      }
      submitData.priority = null;
      submitData.subPagePriority = submitData.subPagePriority ?? null;
    }

    const onSuccess = () => {
      showToast({
        message: edit ? 'Page updated successfully' : 'Page added successfully',
        status: EToasterStatus.SUCCESS
      });
      getPages.refetch();
      onCloseForm();
    };

    const onError = () => {
      showToast({
        message: edit ? 'Error updating page' : 'Error adding page',
        status: EToasterStatus.ERROR
      });
    };

    if (edit && pageId && !dup) {
      updatePage.mutate({ form: submitData, pageId }, { onSuccess, onError });
    } else {
      addPage.mutate(submitData, { onSuccess, onError });
    }
  };

  const handleFormSubmit = () => {
    form.handleSubmit(onSubmit)();
  };

  const handleOptionChange = (option: EPagesRadioButtonOptions) => {
    dispatch(
      openModal({
        currentModal: PORTAL_MODAL_NAMES.PUBLISH_PAGE,
        modalActions: {
          onConfirm: {
            label: 'Save changes',
            action: () => {
              form.setValue('state', option, {
                shouldDirty: true,
                shouldValidate: true
              });
              if (option === EPagesRadioButtonOptions.HIDDEN) {
                form.setValue('publishDate', '', {
                  shouldDirty: true,
                  shouldValidate: true
                });
              }

              if (option === EPagesRadioButtonOptions.VISIBLE) {
                form.setValue('publishDate', '', {
                  shouldDirty: true,
                  shouldValidate: true
                });
                form.setValue('isPublishDateEnabled', false, {
                  shouldDirty: true,
                  shouldValidate: true
                });
              }
              dispatch(closeModal());
            }
          },
          onClose: {
            label: 'Not Now',
            action: () => {
              dispatch(closeModal());
            },
            variant: 'link'
          }
        }
      })
    );
  };

  return (
    <div
      className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-scroll"
      data-testid="PagesForm-Wrapper"
    >
      {(edit || dup) && getPage.isLoading && (
        <div
          className="absolute w-full h-full top-0 left-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm"
          data-testid="PagesForm-Loading"
        >
          <CircularProgress />
        </div>
      )}
      <FormHeader
        dataTestId="PagesForm-Header"
        title="Create Portal Page"
        mainButtonText={BUTTON_TEXT}
        onCloseForm={onCloseForm}
        onMainButtonClick={handleFormSubmit}
        closeFormOnSubmit={false}
        onOptionChange={(option) =>
          handleOptionChange(option as EPagesRadioButtonOptions)
        }
        selectedOption={form.watch('state')}
        options={PAGES_RADIO_BUTTON_OPTIONS}
        isFormValid={isUpdateButtonEnabled}
      />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mt-7 mb-9 w-[456px] mx-auto"
          data-testid="PagesForm"
        >
          <RichText
            title="Page Settings"
            className="mb-9"
            data-testid="PagesForm-SettingsRichText"
          />
          <FormField
            control={form.control}
            name="pageName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    isRequired
                    label="Page Name"
                    placeholder="e.g., Blog"
                    state={errors.pageName?.message ? 'error' : 'default'}
                    description={errors.pageName?.message}
                    {...field}
                    className="mb-5"
                    data-testid="PagesForm-PageName"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="pageIcon"
            render={({ field: { onChange, value } }) => (
              <FormItem>
                <FormControl>
                  <UploadImage
                    title="Add an icon"
                    description={`Add an icon to your page.\nImage will be scaled to 130px x 60px`}
                    buttonText="Upload"
                    onUpload={(url: string) => {
                      onChange(url);
                    }}
                    className={cn(
                      'w-full mb-5',
                      errors.pageIcon?.message ? 'mb-0' : ''
                    )}
                    isRequired
                    selectedImageUrl={value}
                    data-testid="PagesForm-Upload-PageIcon"
                  />
                </FormControl>
                <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
              </FormItem>
            )}
          />
          <div
            className="grid grid-cols-2 gap-5 mb-9 border-b pb-[50px] border-base-sidebarBorder w-full"
            data-testid="PagesForm-Grid"
          >
            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => {
                const { value, onChange, onBlur, ...rest } = field;
                return (
                  <FormItem>
                    <FormControl>
                      <Input
                        label="Portal/"
                        placeholder="e.g., blog"
                        value={value}
                        onChange={onChange}
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                          form.setValue('slug', sanitizeSlug(e.target.value), {
                            shouldDirty: true,
                            shouldValidate: true
                          });
                        }}
                        description={errors.slug?.message}
                        state={errors.slug?.message ? 'error' : 'default'}
                        {...rest}
                        disabled={isPageTemplateDisabled}
                        data-testid="PagesForm-Input-Slug"
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="pageTemplate"
              render={({ field: { onChange } }) => (
                <FormItem>
                  <FormControl>
                    <InputSelectDropdown
                      label="Page Type"
                      defaultSelected={pageTemplateOptions.find(
                        (option) => option.value === form.watch('pageTemplate')
                      )}
                      options={pageTemplateOptions}
                      onOptionChange={onChange}
                      disabled={isPageTemplateDisabled}
                      buttonDataTestId="PagesForm-Select-PageType"
                      listDataTestId="PagesForm-Select-PageType-List"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isSubPage"
              render={({ field: { onChange, value } }) => (
                <FormItem>
                  <FormControl>
                    <InputSelectDropdown
                      label="Page Level"
                      defaultSelected={PAGES_LEVEL_OPTIONS.find(
                        (option) => option.value === String(value)
                      )}
                      options={PAGES_LEVEL_OPTIONS}
                      onOptionChange={(selectedValue) => {
                        handleIsSubPageChange(selectedValue, onChange);
                      }}
                      disabled={isPageTemplateDisabled}
                      buttonDataTestId="PagesForm-Select-PageLevel"
                      listDataTestId="PagesForm-Select-PageLevel-List"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="parentPageId"
              render={({ field: { onChange, value } }) => (
                <FormItem>
                  <FormControl>
                    <InputSelectDropdown
                      isRequired={form.watch('isSubPage')}
                      key={`parent-page-${form.watch('isSubPage')}-${value || 'none'}`}
                      disabled={
                        !form.watch('isSubPage') || isPageTemplateDisabled
                      }
                      label="Parent Page"
                      defaultSelected={
                        form.watch('isSubPage') && value
                          ? mainPages.find(
                              (option: any) => option.value === value
                            )
                          : undefined
                      }
                      options={mainPages}
                      onOptionChange={onChange}
                      placeholder="Select parent page"
                      buttonDataTestId="PagesForm-Select-ParentPage"
                      listDataTestId="PagesForm-Select-ParentPage-List"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {!form.watch('isSubPage') ? (
              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        label="Priority"
                        {...field}
                        value={field.value ?? ''}
                        data-testid="PagesForm-Input-Priority"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={form.control}
                name="subPagePriority"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        label="Priority"
                        {...field}
                        value={field.value ?? ''}
                        data-testid="PagesForm-Input-SubPagePriority"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="visibility"
              render={({ field: { onChange } }) => (
                <FormItem>
                  <FormControl>
                    <InputSelectDropdown
                      label="Visibility"
                      defaultSelected={visibilityOptions.find(
                        (option) => option.value === form.watch('visibility')
                      )}
                      options={visibilityOptions}
                      onOptionChange={onChange}
                      buttonDataTestId="PagesForm-Select-Visibility"
                      listDataTestId="PagesForm-Select-Visibility-List"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <RichText
            title="Metadata"
            description="Enhance your page's visibility and searchability"
            className="mb-9"
            data-testid="PagesForm-MetadataRichText"
          />
          <FormField
            control={form.control}
            name="metadataImage"
            render={({ field: { onChange, value } }) => (
              <FormItem>
                <FormControl>
                  <UploadImage
                    title="Add an image"
                    description="The image will be scaled to 32px x 32px"
                    buttonText="Upload"
                    onUpload={(url: string) => {
                      onChange(url);
                    }}
                    className="w-full mb-5"
                    selectedImageUrl={value}
                    data-testid="PagesForm-Upload-MetadataImage"
                    buttonDataTestId="PagesForm-Upload-MetadataImage-Btn"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="metadataDescription"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    label="Metadata Description"
                    placeholder="Write Something..."
                    {...field}
                    data-testid="PagesForm-Textarea-MetadataDescription"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.watch('state') === EPagesRadioButtonOptions.HIDDEN && (
            <div
              className="w-full mt-9"
              data-testid="PagesForm-ScheduleSection"
            >
              <RichText
                title="Schedule publication date"
                description="Set a date for your content to go live"
                className="mb-5"
              />
              <FormField
                control={form.control}
                name="isPublishDateEnabled"
                render={({ field }) => (
                  <Switch
                    key={field.value ? 'on' : 'off'}
                    className="mb-9"
                    label="Enable scheduled publishing"
                    description="Turn on scheduled publishing for this content"
                    textAlignment="right"
                    defaultChecked={field.value}
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      if (!checked) {
                        form.setValue('publishDate', '', {
                          shouldDirty: true,
                          shouldValidate: true
                        });
                      }
                    }}
                    data-testid="PagesForm-Switch-Schedule"
                  />
                )}
              />
              <FormField
                name="publishDate"
                control={form.control}
                render={({ field }) => {
                  return (
                    <FormItem>
                      <DatePicker
                        shouldExcludePast
                        displayInUTC
                        label="Publish date"
                        infoTooltipContent="All times shown in UTC"
                        disabled={!form.watch('isPublishDateEnabled')}
                        {...field}
                        dataTestId="PagesForm-DatePicker-PublishDate"
                      />
                    </FormItem>
                  );
                }}
              />
            </div>
          )}
        </form>
      </Form>
    </div>
  );
};

export default PagesForm;
